import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ZonedDateTime } from '@internationalized/date';
import { cn } from '@antimatterhq/ui/lib/utils';
import { useAtomValue } from 'jotai';
import {
  histogramTimelineDragOffsetAtom,
  histogramVisibleTimeRangeAtom,
} from '../jotai/histogramAtoms';
import {
  getIntervalMinutes,
  generateTimeTicks,
} from './histogramTimeline.utils';

type HistogramTimelineProps = {
  timezone?: string;
  onTimeRangeChange?: (start: ZonedDateTime, end: ZonedDateTime) => void;
  onDragStart?: (startX: number) => void;
  onDragMove?: (deltaX: number) => void;
  onDragEnd?: () => void;
  dragOffset?: number; // External drag offset to override internal state
  className?: string;
};

const HistogramTimeline = ({
  timezone = 'UTC',
  onDragStart,
  onDragMove,
  onDragEnd,
  className,
}: HistogramTimelineProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 20 });
  const [isLargeWidth, setIsLargeWidth] = useState(false);
  const [startTime, endTime] = useAtomValue(histogramVisibleTimeRangeAtom);
  const externalDragOffset = useAtomValue(histogramTimelineDragOffsetAtom);

  // Calculate time range duration in minutes
  const durationMs = endTime.toDate().getTime() - startTime.toDate().getTime();
  const durationMinutes = durationMs / (1000 * 60);

  // Get appropriate interval
  const intervalMinutes = getIntervalMinutes(durationMinutes, isLargeWidth);

  // Create a stable reference time that doesn't change during drag operations
  // For multi-day intervals, always use a fixed date (Jan 1, 2024) to ensure consistency
  const stableReferenceTime = useRef<ZonedDateTime | null>(null);
  if (!stableReferenceTime.current) {
    if (intervalMinutes >= 24 * 60) {
      // For multi-day intervals, use a fixed reference date in the same timezone
      stableReferenceTime.current = startTime.set({
        year: 2024,
        month: 1,
        day: 1,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
    } else {
      // For smaller intervals, use the initial startTime at midnight
      stableReferenceTime.current = startTime.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });

      // For intervals >= 60 minutes, align to the start of the hour
      if (intervalMinutes >= 60) {
        stableReferenceTime.current = stableReferenceTime.current.set({
          minute: 0,
        });
      }
    }
  }

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);

  // Update dimensions when container resizes
  useEffect(() => {
    const updateDimensions = () => {
      if (svgRef.current) {
        const { width } = svgRef.current.getBoundingClientRect();
        setDimensions({ width, height: 20 });
        setIsLargeWidth(width > 900);
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);

    if (svgRef.current) {
      resizeObserver.observe(svgRef.current.parentElement!);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    onDragStart?.(e.clientX);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - dragStartX.current;
      // Let the parent calculate the offset and handle constraints
      onDragMove?.(deltaX);
    },
    [isDragging, dragStartX, onDragMove],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onDragEnd?.();
    }
  }, [isDragging, onDragEnd]);

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Use external drag offset if provided, otherwise 0
  const activeDragOffset = externalDragOffset ?? 0;

  // Generate ticks for a much wider range around the ACTUAL visible time range
  // Use startTime/endTime (not the midnight reference) to ensure ticks are in the right area
  const bufferMs = durationMs * 3; // 3x buffer on each side for stability
  const extendedStartTime = startTime.subtract({ milliseconds: bufferMs });
  const extendedEndTime = endTime.add({ milliseconds: bufferMs });
  const allTicks = generateTimeTicks(
    extendedStartTime,
    extendedEndTime,
    intervalMinutes,
    timezone,
  );

  // Use the stable reference time for positioning calculations
  const referenceTime = stableReferenceTime.current!;

  return (
    <div className={cn('relative h-5 w-full', className)}>
      <svg
        ref={svgRef}
        width="100%"
        height={dimensions.height}
        className={cn(
          'transition-colors hover:bg-blue-50 dark:hover:bg-blue-500/20',
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
        style={{ touchAction: 'none' }}
        onMouseDown={handleMouseDown}
      >
        {/* Background */}
        <rect width="100%" height={dimensions.height} fill="transparent" />

        {/* Baseline */}
        <line
          x1={0}
          y1={0}
          x2={dimensions.width}
          y2={0}
          stroke="currentColor"
          strokeWidth={1}
          className="text-gray-300 dark:text-gray-700"
        />

        {/* Time axis group - translate based on drag offset */}
        <g
          className="time-axis"
          transform={`translate(${activeDragOffset}, 0)`}
        >
          {allTicks.map((tick) => {
            // Calculate x position relative to the stable reference point
            const tickOffsetMs =
              tick.time.toDate().getTime() - referenceTime.toDate().getTime();
            const referenceOffsetMs =
              startTime.toDate().getTime() - referenceTime.toDate().getTime();
            const x =
              ((tickOffsetMs - referenceOffsetMs) / durationMs) *
              dimensions.width;

            // Only render ticks that would be visible (with some padding)
            const visibleX = x + activeDragOffset;
            if (visibleX < -50 || visibleX > dimensions.width + 50) return null;

            return (
              <g
                key={tick.time.toDate().getTime()}
                transform={`translate(${x}, 0)`}
              >
                {/* Tick line */}
                <line
                  y1={0}
                  y2={5}
                  stroke="currentColor"
                  strokeWidth={1}
                  className="text-gray-400 dark:text-gray-600"
                />
                {/* Time label */}
                <text
                  y={14}
                  textAnchor="middle"
                  className="fill-gray-600 text-[10px] dark:fill-gray-400"
                  style={{ userSelect: 'none' }}
                >
                  {tick.label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export { HistogramTimeline };
